/**
 * Node Preview - Dev Tool
 *
 * Renders the full MatchLobbyLayout with mock data for polishing the player
 * experience. Controls live in a floating sidebar panel.
 * Only accessible in development mode.
 */

import type { FlowContext, FlowPlayer, NodeState } from "@rivaled/shared";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PreviewControlPanel } from "../../../components/PreviewControlPanel";
import { trpc } from "../../../lib/trpc";
import { TestPlayerProvider } from "../../../match-flow/hooks/useCurrentPlayerId";
import { MatchLobbyLayout } from "../../../match-flow/layout/MatchLobbyLayout";
import { NODE_MOCKS, NODE_PRESETS, NODE_TYPES } from "../../../match-flow/nodes/all-mocks";
import type { StatePreset } from "../../../match-flow/nodes/mock-types";

export type ContextToggles = {
  teams: boolean;
  host: boolean;
  selectedMap: boolean;
  captains: boolean;
  bannedMaps: boolean;
};

export type TimeoutPreset = "default" | "half" | "expiring" | "expired" | "none";

const DEFAULT_CONTEXT_TOGGLES: ContextToggles = {
  teams: true,
  host: true,
  selectedMap: false,
  captains: false,
  bannedMaps: false,
};

type NodePreviewSearch = {
  node: string;
  clubId?: string;
  queueId?: string;
  players?: number;
  teamSize?: number;
  parties?: number;
  inParty?: boolean;
  viewport?: string;
  preset?: string;
};

function getSearchString(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

export const Route = createFileRoute("/app/dev/node-preview")({
  staticData: { title: "Node Preview" },
  validateSearch: (search: Record<string, unknown>): NodePreviewSearch => {
    const node = search.node as string;
    const players = search.players != null ? Number(search.players) : undefined;
    const teamSize = search.teamSize != null ? Number(search.teamSize) : undefined;
    const parties = search.parties != null ? Number(search.parties) : undefined;
    const inParty = search.inParty === true || search.inParty === "true";
    const viewport = search.viewport as string | undefined;
    const clubId = getSearchString(search.clubId);
    const queueId = getSearchString(search.queueId);
    const preset = getSearchString(search.preset);
    return {
      node: node && NODE_TYPES.includes(node) ? node : "ready_up",
      ...(clubId ? { clubId } : {}),
      ...(queueId ? { queueId } : {}),
      ...(players != null && !Number.isNaN(players) && players >= 2 && players <= 100
        ? { players }
        : {}),
      ...(teamSize != null && !Number.isNaN(teamSize) && teamSize >= 1 && teamSize <= 50
        ? { teamSize }
        : {}),
      ...(parties != null && !Number.isNaN(parties) && parties >= 0 && parties <= 10
        ? { parties }
        : {}),
      ...(inParty ? { inParty: true } : {}),
      ...(viewport && ["desktop", "tablet", "mobile", "all"].includes(viewport)
        ? { viewport }
        : {}),
      ...(preset ? { preset } : {}),
    };
  },
  component: NodePreviewPage,
});

// ---------------------------------------------------------------------------
// Mock player generator
// ---------------------------------------------------------------------------

const MOCK_NAMES = [
  "xNightmare_97",
  "ToxicBarrel",
  "ShadowReaper_X",
  "gg_ez_clap",
  "FragMachine420",
  "IceColdSniper22",
  "BunnyHopKing",
  "N0sc0pe_Larry",
  "SilentStormX",
  "PixelDemonTTV",
  "ThunderStr1ke_",
  "CrispyBacon99",
  "AimAssist_Andy",
  "VelocityRush",
  "DarkMatter_XD",
  "ClutchOrKick",
  "RetroWave_88",
  "NeonPhantomX",
  "ZeroGravity__",
  "BlinkDagger",
  "CyberVortex",
  "omega_wolf_",
  "RunNGunSteve",
  "HeadshotHeroo",
  "QuantumLeap_23",
  "TurboNerd_42",
  "GhostPepper_",
  "RocketJumper",
  "CaptainClutch",
  "xX_DragonSlayer",
  "NoobMaster_69",
  "SweatLord_TTV",
  "ViperStrike_",
  "MoonlitSamurai",
  "gg_no_re_pls",
  "FlickMaster_Pro",
  "CoffeeAndFrags",
  "DoomBringer_X",
  "ChillVibesOnly",
  "TiltedTowers99",
  "OneDeag_Steve",
  "PeekAboo_lol",
  "RushB_Cyka",
  "WallBang_Willie",
  "SmokeDefuse_",
  "Ninja_Defuser",
  "BottomFragGod",
  "CrosshairLarry",
  "PopFlash_Pete",
  "AceSeason_",
];

/** Simple seeded PRNG for deterministic mock data */
function seededRandom(seed: number) {
  let s = Math.abs(seed) || 1;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function createMockPlayers(
  count: number,
  teamCount: number,
  partyCount: number,
  viewerInParty: boolean,
  viewerId: string,
): FlowPlayer[] {
  const perTeam = Math.ceil(count / teamCount);
  const partyRng = seededRandom(count * 1000 + teamCount * 100 + partyCount * 10);
  const eloRng = seededRandom(count * 7 + teamCount * 13);
  const viewerIdx = parseInt(viewerId.replace("player_", ""), 10) || 0;

  const partyMap = new Map<number, string>();

  if (partyCount > 0) {
    const maxSize = Math.max(2, perTeam);

    for (let p = 0; p < partyCount; p++) {
      const size = 2 + Math.floor(partyRng() * (maxSize - 1));
      const pid = `party_${p}`;
      let added = 0;

      if (p === 0 && viewerInParty) {
        partyMap.set(viewerIdx, pid);
        added = 1;
      }

      for (let i = 0; i < count && added < size; i++) {
        if (partyMap.has(i)) continue;
        if (!viewerInParty && i === viewerIdx) continue;
        partyMap.set(i, pid);
        added++;
      }
    }
  }

  return Array.from({ length: count }, (_, i) => ({
    userId: `player_${i}`,
    username: MOCK_NAMES[i] ?? `Recruit_${String(i + 1).padStart(3, "0")}`,
    avatarUrl: i % 3 !== 2 ? `https://i.pravatar.cc/64?u=player_${i}` : null,
    displayElo: 1200 + Math.round(eloRng() * 600),
    team: Math.min(Math.floor(i / perTeam), teamCount - 1),
    partyId: partyMap.get(i) ?? null,
    isConnected: true,
    isHost: i === 0,
    lastSeenAt: Date.now(),
  }));
}

// ---------------------------------------------------------------------------
// Mock context
// ---------------------------------------------------------------------------

type QueueData = {
  id: string;
  clubId: string;
  clubGameId: string;
  name: string;
  mapPool?: string[] | null;
  iconUrl?: string | null;
  rankConfig?: Record<string, unknown> | null;
};

type ClubGameData = {
  gameId: string | null;
  gameName: string | null;
  iconUrl: string | null;
};

function createMockContext(
  playerCount: number,
  teamCount: number,
  queue?: QueueData | null,
  clubGame?: ClubGameData | null,
): FlowContext {
  const perTeam = Math.ceil(playerCount / teamCount);
  const teams: Record<string, number> = {};
  for (let i = 0; i < playerCount; i++) {
    teams[`player_${i}`] = Math.min(Math.floor(i / perTeam), teamCount - 1);
  }

  return {
    matchId: "preview_mock",
    queueId: queue?.id ?? "queue_1",
    clubId: queue?.clubId ?? "club_1",
    clubGameId: queue?.clubGameId ?? "game_1",
    gameId: clubGame?.gameId ?? "game_1",
    mapPool: queue?.mapPool ?? ["map_1", "map_2", "map_3", "map_4", "map_5"],
    teamCount,
    teamSize: perTeam,
    teams,
    hostId: "player_0",
    queueName: queue?.name,
    queueIconUrl: queue?.iconUrl,
    gameName: clubGame?.gameName ?? undefined,
    gameIconUrl: clubGame?.iconUrl ?? null,
    rankConfig: (queue?.rankConfig as FlowContext["rankConfig"]) ?? {
      thresholds: {
        unranked: 0,
        bronze: 100,
        silver: 400,
        gold: 800,
        platinum: 1200,
        diamond: 1600,
        prime: 2000,
        champion: 2400,
      },
      primeTopN: null,
      championTopN: null,
    },
  };
}

const VIEWPORT_SIZES: Record<string, { label: string; width: number }> = {
  desktop: { label: "Desktop", width: 1080 },
  tablet: { label: "Tablet", width: 768 },
  mobile: { label: "Mobile", width: 375 },
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function NodePreviewPage() {
  if (!import.meta.env.DEV) {
    return <Navigate to="/app" />;
  }

  return <NodePreviewContent />;
}

function NodePreviewContent() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  // Panel state
  const [panelOpen, setPanelOpen] = useState(true);

  // Derived from search params
  const nodeType = search.node;
  const teamSize = search.teamSize ?? 5;
  const rawPlayerCount = search.players ?? teamSize * 2;
  const maxPlayers = Math.floor(100 / teamSize) * teamSize;
  const playerCount = Math.max(
    teamSize,
    Math.min(Math.round(rawPlayerCount / teamSize) * teamSize, maxPlayers),
  );
  const teamCount = Math.max(1, playerCount / teamSize);
  const partyCount = search.parties ?? 0;
  const viewerInParty = search.inParty === true && partyCount > 0;
  const viewport = (search.viewport ?? "desktop") as string;
  const presetFromUrl = search.preset ?? null;

  // Local state
  const [selectedPlayerId, setSelectedPlayerId] = useState("player_0");
  const [stateOverride, setStateOverride] = useState<string | null>(null);
  const [configOverride, setConfigOverride] = useState<string | null>(null);
  const [actionLog, setActionLog] = useState<string[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(presetFromUrl);
  const [contextToggles, setContextToggles] = useState<ContextToggles>(DEFAULT_CONTEXT_TOGGLES);
  const [timeoutPreset, setTimeoutPreset] = useState<TimeoutPreset>("default");

  // -------------------------------------------------------------------------
  // Real data fetching
  // -------------------------------------------------------------------------

  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const selectedClubId = search.clubId ?? myClubs?.[0]?.id ?? "";

  const { data: queuesData } = trpc.queue.list.useQuery(
    { clubId: selectedClubId },
    { enabled: !!selectedClubId },
  );
  const selectedQueueId = search.queueId ?? queuesData?.[0]?.id ?? "";

  const selectedQueue = queuesData?.find((q) => q.id === selectedQueueId);

  const { data: clubGames } = trpc.clubGame.list.useQuery(
    { clubId: selectedClubId },
    { enabled: !!selectedClubId },
  );
  const selectedClubGame = clubGames?.find(
    (cg: Record<string, unknown>) => cg.id === selectedQueue?.clubGameId,
  );

  // Auto-select first club
  useEffect(() => {
    if (myClubs?.length && !search.clubId) {
      updateSearch({ clubId: myClubs[0].id });
    }
  }, [myClubs?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first queue
  useEffect(() => {
    if (queuesData?.length && !search.queueId) {
      updateSearch({ queueId: queuesData[0].id });
    }
  }, [queuesData?.length, selectedClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Search param helpers
  // -------------------------------------------------------------------------

  const updateSearch = useCallback(
    (updates: Partial<NodePreviewSearch>) => {
      void navigate({
        to: "/app/dev/node-preview",
        search: { ...search, ...updates },
        replace: true,
      });
    },
    [navigate, search],
  );

  // -------------------------------------------------------------------------
  // Mock data
  // -------------------------------------------------------------------------

  const mock = NODE_MOCKS[nodeType];
  const players = useMemo(
    () => createMockPlayers(playerCount, teamCount, partyCount, viewerInParty, selectedPlayerId),
    [playerCount, teamCount, partyCount, viewerInParty, selectedPlayerId],
  );
  const context = useMemo(
    () =>
      createMockContext(
        playerCount,
        teamCount,
        selectedQueue as QueueData | undefined,
        selectedClubGame as ClubGameData | undefined,
      ),
    [playerCount, teamCount, selectedQueue, selectedClubGame],
  );

  // Apply context toggles — strip fields that are toggled off
  const effectiveContext = useMemo((): FlowContext => {
    const ctx = { ...context };
    if (!contextToggles.teams) {
      delete ctx.teams;
    }
    if (!contextToggles.host) {
      delete ctx.hostId;
    }
    if (!contextToggles.selectedMap) {
      delete ctx.selectedMapId;
      delete ctx.selectedMapName;
    } else if (!ctx.selectedMapId && ctx.mapPool?.length) {
      // Auto-fill a selected map so the context bar can show it
      ctx.selectedMapId = ctx.mapPool[0];
    }
    if (!contextToggles.captains) {
      delete ctx.captains;
    } else if (!ctx.captains) {
      ctx.captains = [players[0]?.userId, players[1]?.userId].filter(Boolean);
    }
    if (!contextToggles.bannedMaps) {
      delete ctx.bannedMaps;
    } else if (!ctx.bannedMaps && ctx.mapPool?.length) {
      ctx.bannedMaps = ctx.mapPool.slice(0, 2);
    }
    return ctx;
  }, [context, contextToggles, players]);

  // Strip team assignments from players when teams toggled off
  const effectivePlayers = useMemo(() => {
    if (contextToggles.teams) return players;
    return players.map((p) => ({ ...p, team: -1 }));
  }, [players, contextToggles.teams]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleNodeTypeChange = useCallback(
    (type: string) => {
      updateSearch({ node: type, preset: undefined });
      setStateOverride(null);
      setConfigOverride(null);
      setActionLog([]);
      setActivePreset(null);
    },
    [updateSearch],
  );

  const handlePreset = useCallback(
    (preset: StatePreset | null) => {
      if (!preset) {
        setStateOverride(null);
        setActivePreset(null);
        updateSearch({ preset: undefined });
        return;
      }
      const defaultState = mock.state(players, teamCount);
      const result = preset.build({
        players,
        teamCount,
        viewerId: selectedPlayerId,
        defaultState,
      });
      setStateOverride(JSON.stringify(result.state, null, 2));
      setActivePreset(preset.label);
      updateSearch({ preset: preset.label });
      if (result.viewAs) {
        setSelectedPlayerId(result.viewAs);
      }
    },
    [mock, players, teamCount, selectedPlayerId, updateSearch],
  );

  // Auto-apply preset from URL
  useEffect(() => {
    if (!presetFromUrl) return;
    const presets = NODE_PRESETS[nodeType] ?? [];
    const matched = presets.find((p) => p.label === presetFromUrl);
    if (matched) {
      const defaultState = mock.state(players, teamCount);
      const result = matched.build({
        players,
        teamCount,
        viewerId: selectedPlayerId,
        defaultState,
      });
      setStateOverride(JSON.stringify(result.state, null, 2));
      setActivePreset(matched.label);
      if (result.viewAs) {
        setSelectedPlayerId(result.viewAs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeType, teamCount, presetFromUrl, mock.state, players, selectedPlayerId]);

  // Build node state
  const nodeState = useMemo((): NodeState => {
    let state = mock.state(players, teamCount);
    let config = mock.config;

    if (stateOverride) {
      try {
        state = JSON.parse(stateOverride);
      } catch {
        /* keep default */
      }
    }
    if (configOverride) {
      try {
        config = JSON.parse(configOverride);
      } catch {
        /* keep default */
      }
    }

    const timeoutDuration = config.timeoutSeconds
      ? (config.timeoutSeconds as number) * 1000
      : 30_000;
    let timeoutAt: number | null;
    switch (timeoutPreset) {
      case "half":
        timeoutAt = Date.now() + timeoutDuration / 2;
        break;
      case "expiring":
        timeoutAt = Date.now() + 5_000;
        break;
      case "expired":
        timeoutAt = Date.now() - 1_000;
        break;
      case "none":
        timeoutAt = null;
        break;
      default:
        timeoutAt = Date.now() + timeoutDuration;
    }

    return {
      nodeId: "preview_node",
      nodeType,
      status: "active",
      config,
      state,
      startedAt: Date.now(),
      completedAt: null,
      timeoutAt,
      error: null,
    };
  }, [nodeType, players, mock, stateOverride, configOverride, teamCount, timeoutPreset]);

  // Mock action handler
  const handleAction = useCallback(
    async (type: string, payload: unknown) => {
      const entry = `${type}: ${JSON.stringify(payload)}`;
      setActionLog((prev) => [entry, ...prev].slice(0, 20));

      let current: Record<string, unknown>;
      if (stateOverride) {
        try {
          current = JSON.parse(stateOverride);
        } catch {
          current = mock.state(players, teamCount);
        }
      } else {
        current = mock.state(players, teamCount);
      }

      const p = payload as Record<string, unknown>;
      const next = computeNextState(nodeType, current, p, players, context, teamCount);

      if (next) {
        setStateOverride(JSON.stringify(next, null, 2));
        setActivePreset(null);
      }

      return { success: true };
    },
    [nodeType, stateOverride, mock, players, context, teamCount],
  );

  // Timer tick
  useEffect(() => {
    if (!nodeState.timeoutAt) {
      setTimeRemaining(null);
      return;
    }
    const tick = () => {
      setTimeRemaining(Math.max(0, nodeState.timeoutAt! - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nodeState.timeoutAt]);

  // Keyboard shortcut to toggle panel
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && panelOpen) {
        setPanelOpen(false);
      }
      if (e.key === "." && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setPanelOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [panelOpen]);

  const stateJson = stateOverride ?? JSON.stringify(mock.state(players, teamCount), null, 2);
  const configJson = configOverride ?? JSON.stringify(mock.config, null, 2);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const matchLayoutProps = {
    players: effectivePlayers,
    context: effectiveContext,
    currentNode: nodeState,
    progress: { completed: 0, total: 1 },
    timeRemaining,
    onAction: handleAction,
    isConnected: true,
  };

  return (
    <div className="min-h-screen bg-surface-950">
      {/* Main canvas */}
      <div className={`transition-all duration-300 min-h-screen ${panelOpen ? "mr-80" : ""}`}>
        {viewport === "all" ? (
          <div className="flex gap-6 overflow-x-auto p-6 items-start justify-center">
            {Object.entries(VIEWPORT_SIZES).map(([key, { label, width }]) => (
              <div
                key={key}
                className="border border-surface-700 rounded-xl bg-surface-950 shadow-lg overflow-hidden shrink-0"
                style={{ width, minWidth: width }}
              >
                <div className="bg-surface-800 border-b border-surface-700 px-3 py-1 flex items-center justify-between">
                  <span className="text-[10px] text-text-secondary font-mono">{width}px</span>
                  <span className="text-[10px] text-text-secondary">{label}</span>
                </div>
                <div className="p-4">
                  <TestPlayerProvider playerId={selectedPlayerId}>
                    <MatchLobbyLayout {...matchLayoutProps} />
                  </TestPlayerProvider>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="mx-auto p-6 transition-all duration-300"
            style={{
              maxWidth:
                viewport in VIEWPORT_SIZES
                  ? VIEWPORT_SIZES[viewport as keyof typeof VIEWPORT_SIZES].width
                  : undefined,
            }}
          >
            <TestPlayerProvider playerId={selectedPlayerId}>
              <MatchLobbyLayout {...matchLayoutProps} />
            </TestPlayerProvider>
          </div>
        )}
      </div>

      {/* Floating toggle button */}
      {!panelOpen && (
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="fixed bottom-4 right-4 z-50 flex items-center justify-center size-10 rounded-lg bg-surface-800 border border-surface-700 text-text-secondary hover:text-text-primary shadow-lg transition-colors"
          title="Open preview controls (Ctrl+.)"
        >
          <Settings className="size-5" />
        </button>
      )}

      {/* Control panel */}
      {panelOpen && (
        <PreviewControlPanel
          onClose={() => setPanelOpen(false)}
          clubs={myClubs?.map((c) => ({ id: c.id, name: c.name }))}
          queues={queuesData?.map((q) => ({ id: q.id, name: q.name }))}
          clubId={selectedClubId}
          queueId={selectedQueueId}
          onClubChange={(id) => updateSearch({ clubId: id, queueId: undefined })}
          onQueueChange={(id) => updateSearch({ queueId: id })}
          nodeType={nodeType}
          nodeTypes={NODE_TYPES}
          nodeMocks={NODE_MOCKS}
          onNodeTypeChange={handleNodeTypeChange}
          presets={NODE_PRESETS[nodeType] ?? []}
          activePreset={activePreset}
          onPresetChange={handlePreset}
          playerCount={playerCount}
          maxPlayers={maxPlayers}
          teamSize={teamSize}
          teamCount={teamCount}
          onPlayerCountChange={(count) => updateSearch({ players: count })}
          onTeamSizeChange={(size, snapped) => {
            updateSearch({ teamSize: size, players: snapped });
            setStateOverride(null);
          }}
          partyCount={partyCount}
          onPartyCountChange={(count) => updateSearch({ parties: count || undefined })}
          viewerInParty={viewerInParty}
          onViewerInPartyChange={(inParty) => updateSearch({ inParty: inParty || undefined })}
          players={players}
          selectedPlayerId={selectedPlayerId}
          onPlayerIdChange={setSelectedPlayerId}
          viewport={viewport}
          onViewportChange={(v) => updateSearch({ viewport: v })}
          stateJson={stateJson}
          configJson={configJson}
          onStateChange={setStateOverride}
          onConfigChange={setConfigOverride}
          onStateReset={() => {
            setStateOverride(null);
            setActivePreset(null);
          }}
          onConfigReset={() => setConfigOverride(null)}
          actionLog={actionLog}
          onClearLog={() => setActionLog([])}
          contextToggles={contextToggles}
          onContextToggle={(key, value) => setContextToggles((prev) => ({ ...prev, [key]: value }))}
          timeoutPreset={timeoutPreset}
          onTimeoutPresetChange={setTimeoutPreset}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action state transitions (extracted for readability)
// ---------------------------------------------------------------------------

function computeNextState(
  nodeType: string,
  current: Record<string, unknown>,
  p: Record<string, unknown>,
  players: FlowPlayer[],
  context: FlowContext,
  teamCount: number,
): Record<string, unknown> | null {
  if (nodeType === "map_ban" && typeof p.mapId === "string") {
    const bannedMaps = [...(current.bannedMaps as string[])];
    const bansByTeam = JSON.parse(JSON.stringify(current.bansByTeam)) as Record<number, string[]>;
    const team = current.currentTeam as number;
    if (!bannedMaps.includes(p.mapId)) {
      bannedMaps.push(p.mapId);
      bansByTeam[team] = [...(bansByTeam[team] ?? []), p.mapId];
      const teamKeys = Object.keys(bansByTeam).map(Number);
      const nextTeam = teamKeys[(teamKeys.indexOf(team) + 1) % teamKeys.length];
      return {
        ...current,
        bannedMaps,
        bansByTeam,
        banIndex: (current.banIndex as number) + 1,
        currentTeam: nextTeam,
      };
    }
  }

  if (nodeType === "map_vote" && typeof p.mapId === "string") {
    const votes = { ...(current.votes as Record<string, string>) };
    const userId = p.userId as string;
    const oldVote = votes[userId];
    votes[userId] = p.mapId;
    const counts = { ...(current.voteCounts as Record<string, number>) };
    if (oldVote && counts[oldVote] != null) counts[oldVote]--;
    counts[p.mapId] = (counts[p.mapId] ?? 0) + 1;
    return { ...current, votes, voteCounts: counts };
  }

  if (nodeType === "ready_up" && typeof p.userId === "string") {
    const readyPlayers = [...(current.readyPlayers as string[])];
    const notReady = [...(current.notReadyPlayers as string[])];
    if (!readyPlayers.includes(p.userId)) {
      readyPlayers.push(p.userId);
      const idx = notReady.indexOf(p.userId);
      if (idx !== -1) notReady.splice(idx, 1);
      return { ...current, readyPlayers, notReadyPlayers: notReady };
    }
  }

  if (nodeType === "game_mode_select" && typeof p.modeId === "string") {
    const votes = { ...(current.votes as Record<string, string>) };
    const userId = p.userId as string;
    const oldVote = votes[userId];
    votes[userId] = p.modeId;
    const counts = { ...(current.voteCounts as Record<string, number>) };
    if (oldVote && counts[oldVote] != null) counts[oldVote]--;
    counts[p.modeId] = (counts[p.modeId] ?? 0) + 1;
    return { ...current, votes, voteCounts: counts };
  }

  if (nodeType === "side_select" && typeof p.sideId === "string") {
    const sides = current.sides as { id: string; name: string }[];
    const selectingTeam = current.selectingTeam as number;
    const teamSides: Record<number, string> = { [selectingTeam]: p.sideId };
    const remainingSides = sides.filter((s) => s.id !== p.sideId);
    const teamNums = [...new Set(Object.values(context.teams as Record<string, number>))];
    let sideIdx = 0;
    for (const t of teamNums) {
      if (t !== selectingTeam) {
        teamSides[t] = remainingSides[sideIdx % remainingSides.length]?.id ?? sides[0].id;
        sideIdx++;
      }
    }
    return { ...current, teamSides };
  }

  if (nodeType === "captain_draft" && Array.isArray(p.playerIds)) {
    return computeCaptainDraftNext(current, p.playerIds as string[], players, teamCount);
  }

  if (nodeType === "self_select" && typeof p.team === "number" && typeof p.userId === "string") {
    const teams = JSON.parse(JSON.stringify(current.teams)) as Record<number, string[]>;
    const unassigned = [...(current.unassigned as string[])];
    const teamAssignments = { ...(current.teamAssignments as Record<string, number>) };

    const prevTeam = teamAssignments[p.userId];
    if (prevTeam != null && teams[prevTeam]) {
      teams[prevTeam] = teams[prevTeam].filter((id: string) => id !== p.userId);
    }
    if (teams[p.team]) {
      teams[p.team].push(p.userId as string);
    }
    const uIdx = unassigned.indexOf(p.userId as string);
    if (uIdx !== -1) unassigned.splice(uIdx, 1);
    teamAssignments[p.userId as string] = p.team;
    return { ...current, teams, unassigned, teamAssignments };
  }

  if (nodeType === "match_cancelled" && typeof p.userId === "string") {
    const acknowledgedBy = [...(current.acknowledgedBy as string[])];
    if (!acknowledgedBy.includes(p.userId)) {
      acknowledgedBy.push(p.userId);
    }
    return { ...current, acknowledgedBy };
  }

  if (nodeType === "host_instructions" && typeof p.userId === "string") {
    return { ...current, hostConfirmed: true };
  }

  if (nodeType === "lobby_code_input" && p.values && typeof p.values === "object") {
    const values: Record<string, string> = {};
    for (const [k, v] of Object.entries(p.values as Record<string, unknown>)) {
      if (typeof v === "string") values[k] = v.trim();
    }
    return { ...current, values, submittedBy: p.userId };
  }

  if (nodeType === "lobby_created" && typeof p.userId === "string") {
    const confirmedPlayers = [...(current.confirmedPlayers as string[])];
    if (!confirmedPlayers.includes(p.userId)) {
      confirmedPlayers.push(p.userId);
    }
    return { ...current, confirmedPlayers };
  }

  if (nodeType === "character_ban" && typeof p.characterId === "string") {
    const bannedCharacters = [...(current.bannedCharacters as string[])];
    const bansByTeam = JSON.parse(JSON.stringify(current.bansByTeam)) as Record<number, string[]>;
    const team = current.currentTeam as number;
    if (!bannedCharacters.includes(p.characterId)) {
      bannedCharacters.push(p.characterId);
      bansByTeam[team] = [...(bansByTeam[team] ?? []), p.characterId];
      const teamKeys = Object.keys(bansByTeam).map(Number);
      const nextTeam = teamKeys[(teamKeys.indexOf(team) + 1) % teamKeys.length];
      return {
        ...current,
        bannedCharacters,
        bansByTeam,
        currentBanIndex: (current.currentBanIndex as number) + 1,
        currentTeam: nextTeam,
      };
    }
  }

  if (nodeType === "character_pick" && typeof p.characterId === "string") {
    const pickedCharacters = { ...(current.pickedCharacters as Record<string, string>) };
    const userId = p.userId as string;
    if (!pickedCharacters[userId]) {
      pickedCharacters[userId] = p.characterId;
      return {
        ...current,
        pickedCharacters,
        currentPickIndex: (current.currentPickIndex as number) + 1,
      };
    }
  }

  return null;
}

function computeCaptainDraftNext(
  current: Record<string, unknown>,
  playerIds: string[],
  players: FlowPlayer[],
  _teamCount: number,
): Record<string, unknown> | null {
  const pickPool = [...(current.pickPool as string[])];
  const teams = { ...(current.teams as Record<string, number>) };
  const captains = current.captains as string[];
  const draftTeamCount = captains.length;
  const currentTeam = current.currentPickingTeam as number;
  let pickedCount = 0;

  for (const pid of playerIds) {
    const idx = pickPool.indexOf(pid);
    if (idx !== -1) {
      pickPool.splice(idx, 1);
      teams[pid] = currentTeam;
      pickedCount++;
    }
  }

  if (pickedCount === 0) return null;

  const slotsPerTeam = Math.ceil(players.length / draftTeamCount);

  const getTeamSizes = () => {
    const sizes: Record<number, number> = {};
    for (let t = 1; t <= draftTeamCount; t++) sizes[t] = 0;
    for (const t of Object.values(teams)) sizes[t] = (sizes[t] ?? 0) + 1;
    return sizes;
  };

  // Deadlock detection
  let changed = true;
  while (changed) {
    changed = false;
    const sizes = getTeamSizes();
    const poolParties: Record<string, string[]> = {};
    for (const pid of pickPool) {
      const pl = players.find((pp) => pp.userId === pid);
      const key = pl?.partyId ?? `__solo_${pid}`;
      if (!poolParties[key]) poolParties[key] = [];
      poolParties[key].push(pid);
    }
    for (const [, members] of Object.entries(poolParties)) {
      if (members.length < 2) continue;
      const canFit = Object.entries(sizes).some(([, sz]) => slotsPerTeam - sz >= members.length);
      if (!canFit) {
        let bestTeam = 1;
        let bestRoom = 0;
        for (let t = 1; t <= draftTeamCount; t++) {
          const room = slotsPerTeam - sizes[t];
          if (room > bestRoom) {
            bestRoom = room;
            bestTeam = t;
          }
        }
        for (const mid of members) {
          const idx = pickPool.indexOf(mid);
          if (idx !== -1) {
            pickPool.splice(idx, 1);
            teams[mid] = bestTeam;
            pickedCount++;
          }
        }
        changed = true;
        break;
      }
    }
  }

  const teamSizes = getTeamSizes();
  const minSize = Math.min(...Object.values(teamSizes));
  let nextTeam = currentTeam;
  for (let offset = 1; offset <= draftTeamCount; offset++) {
    const candidate = ((currentTeam - 1 + offset) % draftTeamCount) + 1;
    if (teamSizes[candidate] <= minSize) {
      nextTeam = candidate;
      break;
    }
  }

  return {
    ...current,
    pickPool,
    teams,
    pickIndex: (current.pickIndex as number) + pickedCount,
    currentPickingTeam: nextTeam,
  };
}
